import React, { useEffect, useRef, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import message from './utils/messages';
import localizer from './localizer';

import dates from './utils/dates';
import { navigate } from './utils/constants';
import { accessor as get } from './utils/accessors';

import { inRange } from './utils/eventLevels';

const Agenda = ({
  messages: propMessages,
  length = 30,
  date,
  events,
  startAccessor,
  culture,
  components,
  titleAccessor,
  agendaDateFormat,
  endAccessor,
  allDayAccessor,
  agendaTimeRangeFormat,
  agendaTimeFormat,
  onSelectEvent, // Add this prop
}) => {
  const headerRef = useRef(null);
  const dateColRef = useRef(null);
  const timeColRef = useRef(null);
  const contentRef = useRef(null);
  const tbodyRef = useRef(null);
  
  const [columnWidths, setColumnWidths] = useState([]);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);

  const messages = useMemo(() => message(propMessages), [propMessages]);

  // Get scrollbar width on mount
  useEffect(() => {
    const width = getScrollbarSize();
    setScrollbarWidth(width);
  }, []);

  // Adjust header widths when content changes
  useEffect(() => {
    const adjustHeader = () => {
      if (!tbodyRef.current || !headerRef.current) {
        return;
      }

      const firstRow = tbodyRef.current.querySelector('tr');
      if (!firstRow) {
        return;
      }

      const cells = Array.from(firstRow.children);
      const newWidths = cells.map(cell => cell.offsetWidth);

      // Update column widths if changed
      if (JSON.stringify(columnWidths) !== JSON.stringify(newWidths)) {
        setColumnWidths(newWidths);
        
        // Apply widths to header columns
        const headerCells = headerRef.current.querySelectorAll('th');
        headerCells.forEach((th, index) => {
          if (index < newWidths.length) {
            th.style.width = `${newWidths[index]}px`;
            th.style.minWidth = `${newWidths[index]}px`;
            th.style.maxWidth = `${newWidths[index]}px`;
          }
        });
      }

      // Check if content is overflowing
      const contentTable = contentRef.current?.querySelector('table');
      if (contentTable) {
        const isOverflowing = contentTable.scrollHeight > contentRef.current.clientHeight;
        
        if (isOverflowing) {
          headerRef.current.style.paddingRight = `${scrollbarWidth}px`;
          headerRef.current.classList.add('rbc-header-overflowing');
        } else {
          headerRef.current.style.paddingRight = '0';
          headerRef.current.classList.remove('rbc-header-overflowing');
        }
      }
    };

    // Use requestAnimationFrame to ensure DOM is updated
    requestAnimationFrame(() => {
      adjustHeader();
    });
  }, [events, columnWidths, scrollbarWidth]);

  const getScrollbarSize = () => {
    if (typeof window === 'undefined') return 0;
    
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.overflow = 'scroll';
    outer.style.msOverflowStyle = 'scrollbar';
    outer.style.width = '100px';
    outer.style.height = '100px';
    outer.style.position = 'absolute';
    outer.style.top = '-9999px';
    
    document.body.appendChild(outer);
    
    const inner = document.createElement('div');
    inner.style.width = '100%';
    inner.style.height = '200px';
    outer.appendChild(inner);
    
    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
    
    if (outer.parentNode) {
      outer.parentNode.removeChild(outer);
    }
    
    return scrollbarWidth;
  };

  const timeRangeLabel = (day, event) => {
    const TimeComponent = components?.time;
    const start = get(event, startAccessor);
    const end = get(event, endAccessor);

    let labelClass = '';
    let label = messages.allDay;

    if (!get(event, allDayAccessor)) {
      if (dates.eq(start, end, 'day')) {
        label = localizer.format({ start, end }, agendaTimeRangeFormat, culture);
      } else if (dates.eq(day, start, 'day')) {
        label = localizer.format(start, agendaTimeFormat, culture);
      } else if (dates.eq(day, end, 'day')) {
        label = localizer.format(end, agendaTimeFormat, culture);
      }
    }

    if (dates.gt(day, start, 'day')) labelClass = 'rbc-continues-prior';
    if (dates.lt(day, end, 'day')) labelClass += ' rbc-continues-after';

    return (
      <span className={labelClass.trim()}>
        {TimeComponent ? (
          <TimeComponent event={event} label={label} />
        ) : (
          label
        )}
      </span>
    );
  };

  const handleEventClick = (event, e) => {
    e.stopPropagation();
    
    // If onSelectEvent prop is provided, use it
    if (onSelectEvent) {
      onSelectEvent(event);
    }
    
    // You can also directly dispatch here if you prefer
    // dispatch(openEventDetail(event));
  };

  const renderDay = (day, dayEvents, dayKey) => {
    const EventComponent = components?.event;
    const DateComponent = components?.date;

    if (!dayEvents || dayEvents.length === 0) {
      const dateLabel = localizer.format(day, agendaDateFormat, culture);
      return (
        <tr key={dayKey}>
          <td className="rbc-agenda-date-cell">
            {DateComponent ? (
              <DateComponent day={day} label={dateLabel} />
            ) : (
              dateLabel
            )}
          </td>
          <td className="rbc-agenda-time-cell"></td>
          <td className="rbc-agenda-event-cell">
            <em>{messages.noEventsInRange}</em>
          </td>
        </tr>
      );
    }

    return dayEvents.map((event, idx) => {
      const dateLabel = idx === 0 && localizer.format(day, agendaDateFormat, culture);
      const first = idx === 0 ? (
        <td rowSpan={dayEvents.length} className="rbc-agenda-date-cell">
          {DateComponent ? (
            <DateComponent day={day} label={dateLabel} />
          ) : (
            dateLabel
          )}
        </td>
      ) : null;

      const title = get(event, titleAccessor);

      return (
        <tr 
          key={`${dayKey}_${idx}`}
          className="rbc-agenda-row"
        >
          {first}
          <td className="rbc-agenda-time-cell">
            {timeRangeLabel(day, event)}
          </td>
          <td 
            className="rbc-agenda-event-cell"
            onClick={(e) => handleEventClick(event, e)}
            style={{
              cursor: 'pointer',
              position: 'relative'
            }}
          >
            {EventComponent ? (
              <EventComponent 
                event={event} 
                title={title}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEventClick(event, e);
                }}
              />
            ) : (
              <div className="rbc-agenda-event-content">
                {title}
              </div>
            )}
            <div className="rbc-agenda-event-hover-effect"></div>
          </td>
        </tr>
      );
    });
  };

  const end = dates.add(date, length, 'day');
  const range = dates.range(date, end, 'day');

  // Group events by day
  const eventsByDay = range.map(day => {
    const dayEvents = events.filter(event =>
      inRange(event, day, day, { startAccessor, endAccessor, allDayAccessor })
    );
    
    // Sort events by start time
    dayEvents.sort((a, b) => {
      const aStart = get(a, startAccessor);
      const bStart = get(b, startAccessor);
      return aStart - bStart;
    });
    
    return dayEvents;
  });

  return (
    <div className="rbc-agenda-view">
      {/* Header table */}
      <div className="rbc-agenda-header">
        <table ref={headerRef}>
          <thead>
            <tr>
              <th className="rbc-header rbc-agenda-date-cell">
                {messages.date}
              </th>
              <th className="rbc-header rbc-agenda-time-cell">
                {messages.time}
              </th>
              <th className="rbc-header rbc-agenda-event-cell">
                {messages.event}
              </th>
            </tr>
          </thead>
        </table>
      </div>

      {/* Content area with scroll */}
      <div 
        className="rbc-agenda-content" 
        ref={contentRef}
        style={{
          overflowY: 'auto',
          overflowX: 'hidden',
          position: 'relative'
        }}
      >
        <table>
          <tbody ref={tbodyRef}>
            {range.map((day, idx) => 
              renderDay(day, eventsByDay[idx], idx)
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

Agenda.propTypes = {
  messages: PropTypes.shape({
    date: PropTypes.string,
    time: PropTypes.string,
    event: PropTypes.string,
    allDay: PropTypes.string,
    noEventsInRange: PropTypes.string,
  }),
  length: PropTypes.number,
  date: PropTypes.object,
  events: PropTypes.array,
  startAccessor: PropTypes.string,
  culture: PropTypes.string,
  components: PropTypes.object,
  titleAccessor: PropTypes.string,
  agendaDateFormat: PropTypes.string,
  endAccessor: PropTypes.string,
  allDayAccessor: PropTypes.string,
  agendaTimeRangeFormat: PropTypes.func,
  agendaTimeFormat: PropTypes.string,
  onSelectEvent: PropTypes.func, // Add this prop type
};

Agenda.defaultProps = {
  length: 30,
  messages: {
    date: 'Date',
    time: 'Time',
    event: 'Event',
    allDay: 'All Day',
    noEventsInRange: 'No events in this time range.',
  },
  onSelectEvent: null,
};

Agenda.navigate = (date, action) => {
  switch (action) {
    case navigate.PREVIOUS:
      return dates.add(date, -1, 'day');

    case navigate.NEXT:
      return dates.add(date, 1, 'day');

    default:
      return date;
  }
};

Agenda.range = (start, { length = Agenda.defaultProps.length }) => {
  const end = dates.add(start, length, 'day');
  return { start, end };
};

export default Agenda;